import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import PendingSyncBadge from './PendingSyncBadge';
import SessionDegradedBanner from './SessionDegradedBanner';
import { prefetchLikelyRoutes } from '../utils/prefetchRoutes';
import { useAuth } from '../contexts/AuthContext';
import { canAccessAdmin } from '../utils/permissions';
import { useTheme } from '../contexts/ThemeContext';
import { LogOut, User, QrCode, Plus, Archive, Home, Menu, X, Moon, Sun, ShieldCheck, BarChart3, HelpCircle, Sparkles, ClipboardList } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { logger } from '../utils/logger';
import { useHelp } from '../contexts/HelpContext';
import HelpDrawer from './HelpDrawer';
import HelpButton from './HelpButton';
import MobileTabBar from './MobileTabBar';
import WhatsNewModal from './WhatsNewModal';

const Layout: React.FC = () => {
  const { currentUser, role, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { open: openHelp } = useHelp();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  // Warm the chunks for the pages an operator opens most (catalog,
  // barrel detail, tasting, scanner) once the shell is idle — first
  // navigation then hits the module cache instead of the network.
  React.useEffect(() => {
    prefetchLikelyRoutes();
  }, []);

  // Log the live ID-token claims once per sign-in — dev only. In production
  // this would leak auth metadata into every end-user's DevTools.
  React.useEffect(() => {
    if (!currentUser || !import.meta.env.DEV) return;
    currentUser
      .getIdTokenResult()
      .then((t) => {
        logger.debug(
          '[auth] signed in as',
          currentUser.email,
          '→ role:',
          t.claims.role ?? '(none)',
        );
      })
      .catch((err) => {
        logger.warn('[auth] getIdTokenResult failed:', err);
      });
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Failed to log out', error);
    }
  };

  // Kentucky used to be its own nav item (and later a hidden /kentucky
  // route); the Catalog's location tabs handle "show only KY barrels"
  // now, so both the link and the page were removed.
  const navigationItems = [
    { path: '/', label: 'Dashboard', icon: Home },
    // Projects sits between Dashboard and Scan — production workflow
    // for client/group/internal bottling requests. Always visible to
    // any signed-in role; per-action gating happens inside the page
    // (only admins see the "Edit Ranks" mode, etc.).
    { path: '/projects', label: 'Projects', icon: ClipboardList },
    { path: '/scan', label: 'Scan', icon: QrCode },
    { path: '/create', label: 'Create', icon: Plus },
    { path: '/catalog', label: 'Catalog', icon: Archive },
    // Path stays /blends (existing links + bookmarks); the page itself
    // now scopes between blends and all liquids, so the label is broader.
    { path: '/blends', label: 'Liquids', icon: Sparkles },
    { path: '/stats', label: 'Stats', icon: BarChart3 },
    // Admin dashboard is gated by role — admin AND Power User get the
    // link. Power Users see a trimmed dashboard (no Users/Settings)
    // and money fields are filtered out at the tab level. The link
    // label adapts too: Power Users see "Reporting" since that's the
    // slice of the dashboard they actually use.
    ...(canAccessAdmin(role)
      ? [{ path: '/admin', label: role === 'power' ? 'Reporting' : 'Admin', icon: ShieldCheck }]
      : []),
  ];

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleCatalogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/catalog');
  };
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-dark-900 transition-colors duration-200">
      {/* Header. Container widens to `max-w-screen-2xl` (1536px) on
          wide monitors so the brand and user blocks don't float
          orphaned in the middle of a sea of dark. */}
      {/* `pt-safe` (env(safe-area-inset-top)) defensively pads the
          top of the header so the iOS status bar / notch doesn't
          overlap the brand + nav row when the app is launched
          standalone from the home screen. With status-bar-style
          set to "default" the inset is usually 0 anyway, but this
          keeps the header below the notch on iPads in landscape
          and on Android edge-to-edge devices. */}
      <header className="bg-white dark:bg-dark-800 shadow-sm border-b border-gray-100 dark:border-dark-700 sticky top-0 z-50 transition-colors duration-200 pt-safe">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Three-column row: brand (left, shrink-0), nav (center,
              flex-1 + justify-center), user (right, shrink-0). The
              old layout used `justify-between` on three unequal-width
              children which left an awkward gap between brand and
              nav while crowding the right edge — a flex-1 middle
              column keeps the nav visually centered no matter how
              wide the brand or user blocks grow. */}
          <div className="flex items-center h-16 gap-3 lg:gap-4">

            {/* Logo + Name Section. shrink-0 prevents the brand from
                being compressed by the nav row on tablet widths.
                Hover opacity is applied to the TEXT only via
                `group-hover` — applying it to the parent Link would
                create an isolating stacking context that breaks the
                logo's mix-blend-screen trick in dark mode (the
                inverted PNG's black background would leak through). */}
            <Link to="/" className="flex items-center space-x-3 shrink-0 group">
              <img
                src="/image.png"
                alt="The Whiskey Blendery Logo"
                // The PNG ships with a solid white background. We can't
                // edit the asset here, but blend modes drop the white:
                //   - light mode: mix-blend-multiply makes white pixels
                //     transparent against any non-white parent.
                //   - dark mode: dark:invert flips white→black and black→
                //     white, then mix-blend-screen drops the new black
                //     background against the dark header.
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain mix-blend-multiply dark:mix-blend-screen dark:invert"
              />

              {/* Full name only on desktop (lg+, 1024px+). Tablet
                  portrait at 768px doesn't have space for the full
                  name plus 6 nav items plus the user section without
                  the title wrapping awkwardly. */}
              <div className="hidden lg:block leading-tight">
                <h1 className="text-lg xl:text-xl font-bold text-gray-800 dark:text-white whitespace-nowrap group-hover:opacity-80 transition-opacity">
                  The Whiskey Blendery
                </h1>
              </div>

              {/* Abbreviated TWB on mobile + tablet (anything below lg). */}
              <div className="block lg:hidden">
                <h1 className="text-lg font-bold text-gray-800 dark:text-white group-hover:opacity-80 transition-opacity">TWB</h1>
              </div>
            </Link>

            {/* Environment chip. Visible whenever the bundled
             *  Firebase project ID is anything other than the live
             *  prod project — so on staging Vercel builds, on local
             *  dev pointed at the staging project, or on any
             *  misconfigured deploy where the env var is missing
             *  entirely. Removes the "wait, am I editing real client
             *  data right now?" moment when a tab's been open for a
             *  while. Hidden on prod by design. */}
            {import.meta.env.VITE_FIREBASE_PROJECT_ID !== 'the-whiskey-blendery' && (
              <span
                className="ml-2 px-2 py-0.5 rounded bg-amber-500 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wide shrink-0"
                title={`Connected to Firebase project: ${import.meta.env.VITE_FIREBASE_PROJECT_ID || '(unset)'}`}
              >
                Staging
              </span>
            )}

            {/* Desktop Navigation. `flex-1` + `justify-center` makes
                this the elastic middle column — the nav now sits
                visually centered between the brand and user blocks
                regardless of their widths. On smaller (<lg) screens
                the nav collapses to the hamburger menu and the empty
                `flex-1` spacer below keeps the user block pinned to
                the right edge. */}
            <nav className="hidden lg:flex items-center justify-center flex-1 space-x-1">
              {navigationItems.map(({ path, label, icon: Icon }) => (
                path === '/catalog' ? (
                  <button
                    key={path}
                    onClick={handleCatalogClick}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActivePath(path)
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                  </button>
                ) : (
                  <Link
                    key={path}
                    to={path}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActivePath(path)
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                  </Link>
                )
              ))}
              {/* Help opens a slide-over drawer instead of navigating —
                  preserves form state on the underlying page. */}
              <button
                type="button"
                onClick={openHelp}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                title="User guide (?)"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Help</span>
              </button>
            </nav>

            {/* On sub-lg screens the desktop <nav> is hidden, so the
                middle column has no flex-1 occupant. This spacer
                fills the gap and pins the user section to the right
                edge same as on desktop. */}
            <div className="flex-1 lg:hidden" aria-hidden />

            {/* User Section */}
            <div className="flex items-center shrink-0 space-x-2 sm:space-x-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>

              {/* User Info - Hidden on small screens */}
              <div className="hidden lg:block text-right">
                <div className="flex items-center justify-end gap-2">
                  <p className="text-sm font-medium text-gray-800 dark:text-white">
                    {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Signed in'}
                  </p>
                  {role && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                        role === 'admin'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                          : role === 'power'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                      }`}
                      title={`Role: ${role === 'power' ? 'Power User' : role}`}
                    >
                      {role === 'power' ? 'Power' : role === 'user' || role === 'staff' ? 'User' : role}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{currentUser?.email}</p>
              </div>
      
              {/* Writes still syncing to the server (offline / dead-zone
                  saves). Renders nothing when everything is acked. */}
              <PendingSyncBadge />

              {/* User Avatar */}
              <button
                onClick={() => navigate('/profile')}
                className="hidden sm:flex items-center justify-center w-8 h-8 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded-full transition-colors"
                title="View Profile"
              >
                {currentUser?.photoURL ? (
                  <img
                    src={currentUser.photoURL}
                    alt="Profile"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-medium text-white">
                    {currentUser?.displayName?.charAt(0).toUpperCase() || 
                     currentUser?.email?.charAt(0).toUpperCase() || 'D'}
                  </span>
                )}
              </button>

              {/* Mobile Profile Button */}
              <button
                onClick={() => navigate('/profile')}
                className="sm:hidden p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                title="View Profile"
              >
                <User className="w-5 h-5" />
              </button>
      
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200/60 dark:border-dark-700 bg-white dark:bg-dark-800 transition-colors duration-200">
            <nav className="px-4 py-3 space-y-1">
              {navigationItems.map(({ path, label, icon: Icon }) => (
                path === '/catalog' ? (
                  <button
                    key={path}
                    onClick={(e) => {
                      handleCatalogClick(e);
                      closeMobileMenu();
                    }}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium transition-colors w-full text-left ${
                      isActivePath(path)
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{label}</span>
                  </button>
                ) : (
                  <Link
                    key={path}
                    to={path}
                    onClick={closeMobileMenu}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      isActivePath(path)
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{label}</span>
                  </Link>
                )
              ))}
              <button
                type="button"
                onClick={() => {
                  openHelp();
                  closeMobileMenu();
                }}
                className="flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors w-full text-left"
              >
                <HelpCircle className="w-5 h-5" />
                <span>Help</span>
              </button>

              {/* Mobile User Info */}
              <div className="pt-3 mt-3 border-t border-gray-200/60 dark:border-dark-700">
                <Link
                  to="/profile"
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <div>
                    <span className="block">
                      {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Profile'}
                      {role && (
                        <span
                          className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide align-middle ${
                            role === 'admin'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                              : role === 'power'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                          }`}
                        >
                          {role === 'power' ? 'Power' : role === 'user' || role === 'staff' ? 'User' : role}
                        </span>
                      )}
                    </span>
                    <span className="block text-sm text-gray-500 dark:text-gray-400">{currentUser?.email}</span>
                  </div>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content. No max-width here — pages set their own
          (BarrelDetail uses max-w-4xl, Login uses max-w-md, etc.).
          Wrapping in another constraint here caused asymmetric margins
          on tablets because the nested mx-auto chains interacted in
          ways that broke centering.

          pb-20 on phones leaves room for the MobileTabBar (56px tab +
          safe-area). On md+, padding collapses to zero. */}
      {/* Stale auth / App Check token → every Firestore op is denied
          until the tab reloads. Renders nothing while the session is
          healthy; clears itself when the shared subscriptions recover. */}
      <SessionDegradedBanner />

      <main className="pb-20 md:pb-0">
        {/* Route-level error boundary: a crash inside one page shows an
            error panel here while the header + tab bar keep working, so
            the user can navigate away instead of losing the whole app.
            Keyed by pathname so navigating resets the boundary. */}
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Bottom tab bar — phones only, sits under the existing header.
          Five primary destinations + a "More" sheet for Admin/Profile/
          Help/Logout. Routes through the same isActivePath logic as the
          desktop nav so highlights stay in sync. */}
      <MobileTabBar />

      {/* Floating "?" help launcher + slide-over PDF drawer. Mounted
          here (inside Layout) so they only appear on protected routes —
          no need to bother the Login or SetPassword screens. */}
      <HelpButton />
      <HelpDrawer />

      {/* "What's new" popup. Reads the top entry of src/data/releases.ts
          and shows once per device until dismissed — author bumps the
          version field on each release worth announcing. */}
      <WhatsNewModal />
    </div>
  );
};

export default Layout;